import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Col, Input, Popconfirm, Row, Table, Select, RadioChangeEvent, Radio, TablePaginationConfig } from 'antd';
import { ContractorsApi } from 'api/api';
import { ContractorDto, CreateContractorCommand, UpdateContractorCommand } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { Cards } from 'components/cards/frame/cards-frame';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, StyledRadioGroup, TableWrapper, TopToolBox } from 'container/styled';
import { useTranslation } from 'react-i18next';
import { hasPermission } from 'utility/accessibility/hasPermission';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import { ExportButtonPageHeader } from 'components/buttons/export-button/export-button';
import { Link } from 'react-router-dom';
import { CustomTableStyleWrapper } from 'pages/style';
import { ContractorForm } from './ContractorForm';
import { AutoComplete } from '../../components/autoComplete/autoComplete';
import { filterKeys } from 'constants/constants';
import { ProjectsApi } from 'api/api';
import { ProjectDto } from 'api/models';

const { Option } = Select;
const contractorsApi = new ContractorsApi();
const projectsApi = new ProjectsApi();

type ModalState = {
  visible: boolean;
  editMode: boolean;
  selectedContractor: ContractorDto | null;
};

export const ContractorsPage: React.FC = () => {
  const { t } = useTranslation();
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ContractorDto[]>([]);
  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    editMode: false,
    selectedContractor: null,
  });
  const [query, setQuery] = useState({
    searchTerm: '',
    pageNumber: 1,
    pageSize: 10,
    filterType: -1,
    projectId: undefined as number | undefined,
    isActive: undefined as boolean | undefined,
  });
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchContractors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contractorsApi.apiContractorsGet(query);
      setData(response.data.items || []);
      setPagination({
        ...pagination,
        current: query.pageNumber,
        total: response.data.totalCount,
      });
    } catch (err) {
      openNotificationWithIcon('error', t('contractors:fetch-error', 'Failed to fetch contractors'));
    } finally {
      setLoading(false);
    }
  }, [query, t]);

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.apiProjectsGet({ pageSize: -1, isActive: true });
      setProjects(response.data.items || []);
    } catch (err) {
      openNotificationWithIcon('error', t('projects:fetch-error', 'Failed to fetch projects'));
    } finally {
      setProjectsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const columns = [
    {
      title: t('contractors:name', 'Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('clients:phone', 'Phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phoneNumber: string | null) => <span>{phoneNumber || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('contractors:projects', 'Active Projects'),
      dataIndex: 'activeProjectsCount',
      key: 'activeProjectsCount',
    },
    {
      title: t('contractors:pending-payments', 'Pending Payments'),
      dataIndex: 'totalPendingPayments',
      key: 'totalPendingPayments',
      render: (amount: number) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'EUR',
        }).format(amount),
    },
    {
      title: t('contractors:status', 'Status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span className={`status-text ${isActive ? 'active' : 'deactivate'}`}>
          {isActive ? t('global:active', 'Active') : t('global:inactive', 'Inactive')}
        </span>
      ),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 150,
      render: (record: ContractorDto) => (
        <div className="table-actions">
          {hasPermission('contractors:view') && (
            <Link to={`/contractors/${record.id}`}>
              <Button className="btn-icon" type="default" shape="circle">
                <FeatherIcon icon="eye" size={16} />
              </Button>
            </Link>
          )}
          {hasPermission('contractors:payments') && (
            <Link to={`/contractors/${record.id}/payments`}>
              <Button className="btn-icon" type="default" shape="circle">
                <FeatherIcon icon="dollar-sign" size={16} />
              </Button>
            </Link>
          )}
          {hasPermission('contractors:edit') && (
            <Button className="btn-icon" type="primary" shape="circle" onClick={() => handleEdit(record)}>
              <FeatherIcon icon="edit" size={16} />
            </Button>
          )}
          {hasPermission('contractors:delete') && (
            <Popconfirm
              title={t('contractors:delete-confirm', 'Are you sure you want to delete this contractor?')}
              onConfirm={() => handleDelete(record.id as number)}
              okText={t('global:yes', 'Yes')}
              cancelText={t('global:no', 'No')}
            >
              <Button className="btn-icon" type="danger" shape="circle">
                <FeatherIcon icon="trash-2" size={16} />
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setQuery(prev => ({ ...prev, searchTerm: value, pageNumber: 1 }));
    }, 300);
  };

  const resetModalState = () => {
    setModalState({
      visible: false,
      editMode: false,
      selectedContractor: null,
    });
  };

  const handleModalSubmit = async (): Promise<void> => {
    resetModalState();
    await fetchContractors();
  };

  const handleEdit = (contractor: ContractorDto) => {
    setModalState({
      visible: true,
      editMode: true,
      selectedContractor: contractor,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await contractorsApi.apiContractorsIdDelete({ id });
      openNotificationWithIcon('success', t('contractors:deleted', 'Contractor deleted successfully'));
      fetchContractors();
    } catch (err) {
      openNotificationWithIcon('error', t('contractors:delete-error', 'Failed to delete contractor'));
    }
  };

  const handleStatusFilterChange = (value: number) => {
    setQuery(prev => ({
      ...prev,
      filterType: value,
      isActive: value === -1 ? undefined : value === 1,
      pageNumber: 1,
      pageSize: 10,
    }));
  };

  const handleProjectFilterChange = (value: number) => {
    setQuery(prev => ({
      ...prev,
      projectId: value === -1 ? undefined : value,
      pageNumber: 1,
    }));
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('contractors:title', 'Contractors')}
          buttons={[
            <ExportButtonPageHeader
              key="1"
              title={t('contractors:title', 'Contractors')}
              data={data}
              columns={['name', 'activeProjectsCount', 'totalPendingPayments', 'isActive']}
            />,
            hasPermission('contractors:create') && (
              <Button
                key="2"
                type="primary"
                onClick={() => setModalState({ visible: true, editMode: false, selectedContractor: null })}
              >
                <FeatherIcon icon="plus" size={16} />
                {t('contractors:create', 'Add Contractor')}
              </Button>
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  <Col lg={6} md={6} xs={24}>
                    <div className="table-search-box">
                      <AutoComplete
                        onSearch={handleSearch}
                        width="100%"
                        patterns
                        placeholder={t('contractors:search', 'Search contractors...')}
                      />
                    </div>
                  </Col>

                  <Col lg={12} md={14} xs={24}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('projects:project-filter', 'Project:')}</span>
                        <Select
                          style={{ width: 200 }}
                          onChange={handleProjectFilterChange}
                          value={query.projectId ?? -1}
                          loading={projectsLoading}
                        >
                          <Select.Option value={-1}>{t('global:all', 'All')}</Select.Option>
                          {projects.map(project => (
                            <Select.Option key={project.id} value={project.id}>
                              {project.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('users:lable.status', 'Status:')}</span>
                        <Select style={{ width: 200 }} onChange={handleStatusFilterChange} value={query.filterType}>
                          {filterKeys.map(item => (
                            <Select.Option key={item.id} value={item.id}>
                              {t(item.key, item.name)}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </Col>
                </Row>
              </TopToolBox>
            </Col>
          </Row>
          <Row>
            <Cards headless>
              <CustomTableStyleWrapper>
                <TableWrapper>
                  <Table
                    loading={loading}
                    dataSource={data}
                    columns={columns}
                    pagination={{
                      current: query.pageNumber,
                      pageSize: query.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                      pageSizeOptions: [10, 50, 100, 1000],
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} ${t('global.of', 'of')} ${total} ${t('global.items', 'items')}`,
                      onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                    }}
                  />
                </TableWrapper>
              </CustomTableStyleWrapper>
            </Cards>
          </Row>
        </Cards>
      </Main>

      {modalState.visible && (
        <ContractorForm
          visible={modalState.visible}
          editMode={modalState.editMode}
          contractor={modalState.selectedContractor}
          onCancel={() => resetModalState()}
          onSuccess={handleModalSubmit}
        />
      )}
    </>
  );
};

export default ContractorsPage;
